import React, { useEffect, useState, useCallback } from "react";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDialog } from "./admin/ConfirmDialog";
import { HardHat, Plus, Pencil, Trash2, AlertTriangle, GraduationCap, CalendarClock, Users, Activity } from "lucide-react";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c]";
const labelCls = "block text-[12px] font-semibold text-gray-600 mb-1";

const INJURY_TYPES = { sul_lavoro: "Sul lavoro", in_itinere: "In itinere", near_miss: "Near-miss (mancato)" };
const SEVERITIES = { lieve: "Lieve", grave: "Grave", mortale: "Mortale" };
const SEV_CLS = { lieve: "bg-amber-100 text-amber-700", grave: "bg-orange-100 text-orange-700", mortale: "bg-red-100 text-red-700" };

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso; } };
const EMPTY_INJ = { date: "", type: "sul_lavoro", severity: "lieve", days_lost: 0, work_related: true, description: "" };
const EMPTY_TR = { date: "", title: "", hours: 0, participants: 0, description: "" };

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3" data-testid={`safety-stat-${label}`}>
    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "1a", color }}><Icon size={20} /></div>
    <div>
      <div className="text-[22px] font-black leading-none" style={{ color }}>{value}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{label}</div>
    </div>
  </div>
);

const InjuriesTab = ({ reload }) => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => { api.get("/safety/injuries").then((r) => setList(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.date) { toast.error("Inserisci la data"); return; }
    setSaving(true);
    try {
      const body = { ...form, days_lost: Number(form.days_lost) || 0 };
      if (form._edit) { const id = form.id; delete body._edit; delete body.id; await api.put(`/safety/injuries/${id}`, body); toast.success("Infortunio aggiornato"); }
      else { await api.post("/safety/injuries", body); toast.success("Infortunio registrato"); }
      setForm(null); load(); reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/safety/injuries/${toDelete.id}`); toast.success("Infortunio eliminato"); setToDelete(null); load(); reload && reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div data-testid="safety-injuries-tab">
      <div className="flex justify-end mb-4">
        <button onClick={() => setForm({ ...EMPTY_INJ })} data-testid="add-injury-btn" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><Plus size={16} /> Registra infortunio</button>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-bold">Data</th>
              <th className="text-left px-4 py-3 font-bold">Tipo</th>
              <th className="text-left px-4 py-3 font-bold">Gravità</th>
              <th className="text-right px-4 py-3 font-bold">Giorni persi</th>
              <th className="text-left px-4 py-3 font-bold">Correlato lavoro</th>
              <th className="text-left px-4 py-3 font-bold">Descrizione</th>
              <th className="text-right px-4 py-3 font-bold">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {list.map((i) => (
              <tr key={i.id} data-testid={`injury-row-${i.id}`} className="border-b border-gray-100 text-[13px]">
                <td className="px-4 py-3 font-semibold text-[#16345c]">{fmt(i.date)}</td>
                <td className="px-4 py-3">{INJURY_TYPES[i.type] || i.type}</td>
                <td className="px-4 py-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${SEV_CLS[i.severity] || "bg-gray-100 text-gray-600"}`}>{SEVERITIES[i.severity] || i.severity}</span></td>
                <td className="px-4 py-3 text-right">{i.days_lost}</td>
                <td className="px-4 py-3">{i.work_related ? "Sì" : "No"}</td>
                <td className="px-4 py-3 text-gray-600 max-w-[220px] truncate">{i.description || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setForm({ ...i, _edit: true })} data-testid={`injury-edit-${i.id}`} className="text-[#0d6cb8] hover:text-[#0a5590] p-1"><Pencil size={16} /></button>
                  <button onClick={() => setToDelete(i)} data-testid={`injury-delete-${i.id}`} className="text-red-500 hover:text-red-700 p-1 ml-1"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400 text-[13px]">Nessun infortunio registrato. Anche "0 infortuni" è un dato valido per l'ESG.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-[520px]" data-testid="injury-dialog">
          <DialogHeader>
            <DialogTitle>{form?._edit ? "Modifica infortunio" : "Registra infortunio"}</DialogTitle>
            <DialogDescription className="sr-only">Modulo infortunio</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Data *</label>
                <input type="date" value={form.date} data-testid="injury-date" onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Tipo</label>
                <select value={form.type} data-testid="injury-type" onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
                  {Object.entries(INJURY_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Gravità</label>
                <select value={form.severity} data-testid="injury-severity" onChange={(e) => setForm({ ...form, severity: e.target.value })} className={inputCls}>
                  {Object.entries(SEVERITIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Giorni persi</label>
                <input type="number" min="0" value={form.days_lost} data-testid="injury-days" onChange={(e) => setForm({ ...form, days_lost: e.target.value })} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <input type="checkbox" checked={!!form.work_related} data-testid="injury-work-related" onChange={(e) => setForm({ ...form, work_related: e.target.checked })} className="accent-[#16345c] w-4 h-4" />
                  Correlato al lavoro
                </label>
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Descrizione</label>
                <textarea rows={2} value={form.description} data-testid="injury-description" onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setForm(null)} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm">Annulla</button>
            <button onClick={save} disabled={saving} data-testid="injury-save" className="px-4 py-2 text-[13px] font-bold text-white bg-[#16345c] hover:bg-[#0d1b2f] rounded-sm disabled:opacity-60">{saving ? "Salvataggio..." : "Salva"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!toDelete} onCancel={() => setToDelete(null)} onConfirm={remove} title="Eliminare l'infortunio?">
        Stai per eliminare l'infortunio del <b>{toDelete ? fmt(toDelete.date) : ""}</b>. Operazione non reversibile.
      </ConfirmDialog>
    </div>
  );
};

const TrainingTab = ({ reload }) => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => { api.get("/safety/trainings").then((r) => setList(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.date || !form.title.trim()) { toast.error("Inserisci data e titolo"); return; }
    setSaving(true);
    try {
      const body = { ...form, hours: Number(form.hours) || 0, participants: Number(form.participants) || 0 };
      if (form._edit) { const id = form.id; delete body._edit; delete body.id; await api.put(`/safety/trainings/${id}`, body); toast.success("Corso aggiornato"); }
      else { await api.post("/safety/trainings", body); toast.success("Corso registrato"); }
      setForm(null); load(); reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/safety/trainings/${toDelete.id}`); toast.success("Corso eliminato"); setToDelete(null); load(); reload && reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div data-testid="safety-training-tab">
      <div className="flex justify-end mb-4">
        <button onClick={() => setForm({ ...EMPTY_TR })} data-testid="add-training-btn" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><Plus size={16} /> Aggiungi corso sicurezza</button>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-bold">Data</th>
              <th className="text-left px-4 py-3 font-bold">Titolo</th>
              <th className="text-right px-4 py-3 font-bold">Ore</th>
              <th className="text-right px-4 py-3 font-bold">Partecipanti</th>
              <th className="text-left px-4 py-3 font-bold">Note</th>
              <th className="text-right px-4 py-3 font-bold">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {list.map((t) => (
              <tr key={t.id} data-testid={`training-row-${t.id}`} className="border-b border-gray-100 text-[13px]">
                <td className="px-4 py-3 font-semibold text-[#16345c]">{fmt(t.date)}</td>
                <td className="px-4 py-3">{t.title}</td>
                <td className="px-4 py-3 text-right">{t.hours}</td>
                <td className="px-4 py-3 text-right">{t.participants}</td>
                <td className="px-4 py-3 text-gray-600 max-w-[220px] truncate">{t.description || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setForm({ ...t, _edit: true })} data-testid={`training-edit-${t.id}`} className="text-[#0d6cb8] hover:text-[#0a5590] p-1"><Pencil size={16} /></button>
                  <button onClick={() => setToDelete(t)} data-testid={`training-delete-${t.id}`} className="text-red-500 hover:text-red-700 p-1 ml-1"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400 text-[13px]">Nessun corso di formazione sicurezza registrato.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-[520px]" data-testid="training-dialog">
          <DialogHeader>
            <DialogTitle>{form?._edit ? "Modifica corso" : "Nuovo corso sicurezza"}</DialogTitle>
            <DialogDescription className="sr-only">Modulo corso sicurezza</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Data *</label>
                <input type="date" value={form.date} data-testid="training-date" onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Ore</label>
                <input type="number" min="0" step="0.5" value={form.hours} data-testid="training-hours" onChange={(e) => setForm({ ...form, hours: e.target.value })} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Titolo *</label>
                <input value={form.title} data-testid="training-title" onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="es. Corso antincendio rischio medio" />
              </div>
              <div>
                <label className={labelCls}>Partecipanti</label>
                <input type="number" min="0" value={form.participants} data-testid="training-participants" onChange={(e) => setForm({ ...form, participants: e.target.value })} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Note</label>
                <textarea rows={2} value={form.description} data-testid="training-description" onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setForm(null)} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm">Annulla</button>
            <button onClick={save} disabled={saving} data-testid="training-save" className="px-4 py-2 text-[13px] font-bold text-white bg-[#16345c] hover:bg-[#0d1b2f] rounded-sm disabled:opacity-60">{saving ? "Salvataggio..." : "Salva"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!toDelete} onCancel={() => setToDelete(null)} onConfirm={remove} title="Eliminare il corso?">
        Stai per eliminare <b>{toDelete?.title}</b>. Operazione non reversibile.
      </ConfirmDialog>
    </div>
  );
};

const SicurezzaPage = () => {
  const [tab, setTab] = useState("injuries");
  const [summary, setSummary] = useState(null);
  const loadSummary = useCallback(() => { api.get("/safety/summary").then((r) => setSummary(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  return (
    <div className="min-h-screen bg-[#f0f2f5]">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><HardHat size={24} className="text-[#ff7e00]" /> Salute e Sicurezza</h1>
          <p className="text-[14px] text-gray-500">Registro infortuni e formazione sicurezza. I dati aggregati alimentano il dominio ESG "Salute e sicurezza" (VSME B9).</p>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="safety-summary">
            <StatCard icon={AlertTriangle} label={`Infortuni ${summary.year}`} value={summary.total_injuries} color="#c00510" />
            <StatCard icon={CalendarClock} label="Giorni persi" value={summary.total_days_lost} color="#e67e22" />
            <StatCard icon={GraduationCap} label="Ore formazione" value={summary.training_hours} color="#009640" />
            <StatCard icon={Users} label="Partecipanti formazione" value={summary.training_participants} color="#0d6cb8" />
          </div>
        )}

        <div className="flex items-center gap-2 mb-6 border-b border-gray-200">
          {[["injuries", "Infortuni", Activity], ["training", "Formazione sicurezza", GraduationCap]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`safety-tab-${k}`}
              className={"flex items-center gap-2 px-4 py-2.5 text-[13px] font-bold border-b-2 -mb-px transition-colors " + (tab === k ? "border-[#ff7e00] text-[#16345c]" : "border-transparent text-gray-400 hover:text-gray-600")}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {tab === "injuries" ? <InjuriesTab reload={loadSummary} /> : <TrainingTab reload={loadSummary} />}
      </main>
    </div>
  );
};

export default SicurezzaPage;
